// scroll 後新增的新聞區塊
'use client';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import newsClient from '@/common/apiClient/newsClient';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setNewsBar } from '@/slices/popSlice';

import NewsArticle from './NewsArticle'; // 標題摘要 作者資訊
import NewsTitle from './NewsTitle'; // 新聞主要區塊
import anaClient from '@/common/apiClient/anaClient';

const NewsWrapper = ({ locale, firstNews }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const targetRef = useRef(null); // 觀察基準
    const currentIdRef = useRef(firstNews.id); // 當下
    const progressRef = useRef(0); // 閱讀進度

    const [newsList, setNewsList] = useState([firstNews]); // 文章列
    const [loading, setLoading] = useState(false); // 載入更多
    const [viewNewsIndex, setViewNewsIndex] = useState(0); // 正在看的新聞 index

    //   取得更多新聞內容
    const getMoreNews = useCallback(async () => {
        if (loading) return;
        setLoading(true);
        try {
            const result = await newsClient.getMoreNews(locale, currentIdRef.current);
            if (result) {
                setNewsList((prev) => [...prev, result]);
                currentIdRef.current = result.id;
            }
        } catch (e) {
            console.error(e);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    }, [locale, loading]);

    // 處理進度條
    const handleNewsProgress = useCallback(
        (title, progress, newsId) => {
            dispatch(
                setNewsBar({
                    title,
                    progress,
                    newsId,
                })
            );
        },
        [viewNewsIndex, progressRef.current]
    );

    // 處理 scroll 判斷
    const handleScroll = useCallback(async () => {
        const eachNews = document.querySelectorAll('.news-wrapper');
        const position = window.pageYOffset;
        let pageHeight = 0;
        let initTop = 0;
        let title = '';
        let newsId = null;

        for (let i = eachNews.length - 1; i >= 0; i--) {
            if (position >= eachNews[i].offsetTop) {
                initTop = eachNews[i].offsetTop;
                eachNews.forEach((v, index) => {
                    if (index <= i) pageHeight += v.clientHeight;
                });
                title = newsList?.[i]?.title;
                newsId = newsList?.[i]?.id;
                setViewNewsIndex(i);
                break;
            }
        }

        // 進度比例
        const conclusion = ((position - initTop) / (pageHeight - initTop)) * 100;
        handleNewsProgress(title, conclusion, newsId);
    });

    // 文章切換時 變更 url
    const handlePushState = useCallback(() => {
        const queryString = window?.location?.href?.split('?')?.[1];
        const hash = viewNewsIndex === 0 ? window?.location?.hash || '' : '';

        const basePath = locale === 'tw' ? '/news/' : '/cn/news/';
        const slug = newsList?.[viewNewsIndex]?.slug || '';
        const queryAndHash = queryString && viewNewsIndex === 0 ? `?${queryString}${hash}` : hash;

        const path = `${basePath}${slug}${queryAndHash}`;

        history.replaceState({}, '', path), [viewNewsIndex];

        // 建立 news read
        anaClient.createNewsRead(newsList?.[viewNewsIndex]?.id);

        // 不是首篇新聞 下拉後 建立 log
        if (viewNewsIndex > 0) anaClient.createNewsExtendLog({ path, fromNewsId: firstNews.id });
    }, [viewNewsIndex, locale, newsList]);

    // 觀察至底時 fetch 新文章
    useEffect(() => {
        //   觀察器
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !loading) getMoreNews();
                });
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 0.1, // 觀察對象的 10% 進入 viewport
            }
        );
        // 加入觀察
        if (targetRef.current) observer.observe(targetRef.current);

        // 解除觀察
        return () => {
            if (targetRef.current) observer.unobserve(targetRef.current);
        };
    }, [getMoreNews, loading]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [newsList]);

    useEffect(() => {
        handlePushState();
    }, [viewNewsIndex]);

    useEffect(() => {
        const hash = window.location.hash;

        if (hash) {
            const observer = new MutationObserver(() => {
                const targetElement = document.querySelector(hash);
                if (targetElement) {
                    setTimeout(() => {
                        targetElement.scrollIntoView();
                        observer.disconnect(); // 滾動成功後停止監聽
                    }, 100);
                }
            });

            observer.observe(document.body, { childList: true, subtree: true });

            return () => observer.disconnect();
        }
    }, []);

    useEffect(() => {
        if (firstNews)
            dispatch(
                setNewsBar({
                    title: firstNews?.title,
                    progress: 0,
                    newsId: firstNews?.id,
                })
            );
    }, [firstNews]);

    return (
        <>
            {/* 新增列表 */}
            {newsList?.map((news, i) => (
                <React.Fragment key={news?.slug + i + 'extend'}>
                    <NewsArticle news={news} locale={locale}>
                        {/* 標題摘要＋作者資訊 */}
                        <NewsTitle news={news} t={t} locale={locale} />
                    </NewsArticle>
                </React.Fragment>
            ))}

            {loading ? (
                <div className="text-center mx-auto h-[38px]">
                    <img
                        className="w-[130px] inline-block"
                        src="/images/layout/loading.gif"
                        alt="loading"
                    />
                </div>
            ) : (
                // 觀察用
                <hr ref={targetRef} className="opacity-0" />
            )}
        </>
    );
};

export default NewsWrapper;
